import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { ToastController } from '@ionic/angular';
import { Feenics } from './model/model';
import * as localModel from './model/local-model';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root'
})
export class ApiService {

  constructor(
    private httpClient: HttpClient,
    private toastCtrl: ToastController,
    private loadingService: LoadingService
  ) { }

  originURI = '';
  APIURL = localStorage.getItem('server_location');
  rightsResponses: Feenics.Keep.WebApi.Model.VerifyRightsResponse[] = [];
  instance: string = (localStorage.getItem('InstanceInfo')) ? JSON.parse(localStorage.getItem('InstanceInfo')).Key : '';
  access_token: string = localStorage.getItem('access_token');
  clientDeviceId: string = this.makeTemporaryKey(26);
  runningInBrowser = false;

  async presentToast(text) {
    const toast = await this.toastCtrl.create({
      message: text,
      duration: 3000,
      position: 'bottom'
    });
    toast.present();
  }

  makeTemporaryKey(length: number) {
    let text = '';
    const possible = 'abcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }

    return text;
  }

  returnHttpOptions() {
    const httpOptions = {
      headers: new HttpHeaders({
        Authorization: 'Bearer ' + this.access_token,
        'Content-type': 'application/json',
        Accept: 'application/json',
        'Keep-DeviceId': this.clientDeviceId
      })
    };
    return httpOptions;
  }

  returnHttpOptionsForToken() {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-type': 'application/x-www-form-urlencoded',
        Accept: 'application/json'
      })
    };
    return httpOptions;
  }


  handleErrorResponse(name: string, error: HttpErrorResponse, post: boolean) {
    console.error(name + ' error: ', error);
    if (error.status === 401) {
      const scannerName = localStorage.getItem('scannerName');
      const tempDateFormat = localStorage.getItem('selectedDateFormat');
      localStorage.clear();
      localStorage.setItem('scannerName', scannerName);
      if (tempDateFormat !== null) {
        localStorage.setItem('selectedDateFormat', tempDateFormat);
      }
      this.presentToast('Authentication expired. Please log in.');
      return true;
    }
    if (error.status === 400) {
      this.presentToast(`Bad request attempting ${name} ${error.error.Message}. Check Permissions`);
      return false;
    }
    if (error.status === 403) {
      this.presentToast('Permissions error.');
      return false;
    }
    if (error.status === 0) {
      this.presentToast('Network connection failure. Check internet connection.');
      return false;
    }
    if (error.status === 504) {
      this.presentToast('Network request timeout. Check internet connection.');
      return false;
    }
    if (error.status === 404 && post === true) {
      this.presentToast('Network request timeout. Check internet connection.');
      return false;
    }
    if (error.status === 404 && post === false) {
      const errorText = (error.error) ? error.error.Message : error.statusText;
      this.presentToast('Unable to locate requested data.');
      return false;
    }
    if (error.status === 500) {
      this.presentToast('Server error. Please try again.');
      return false;
    }
    if (error.error.Message) {
      this.presentToast(`The following error has occurred: ${error.error.Message}`);
    }
    return false;
  }

  convertDataURIToBinary(dataURI: string) {
    const base64 = dataURI;
    const raw = atob(base64);
    const rawLength = raw.length;
    const array = new Uint8Array(new ArrayBuffer(rawLength));

    for (let i = 0; i < rawLength; i++) {
      array[i] = raw.charCodeAt(i);
    }
    return array;
  }

  getObjectsById(keys: string[]) {
    const callPath = `${this.APIURL}/api/baseInfo`;

    return this.httpClient.post<any[]>(callPath, JSON.stringify(keys), this.returnHttpOptions());
  }

  returnQueryString(queryObject: any): string[] {
    return queryObject.map(x => JSON.stringify(x));
  }

  logOut() {
    this.loadingService.loadingPresent('Logging out.');
    // Store Region and Instance name
    const tempLoginInfo = {
      region: localStorage.getItem('server_location'),
      instanceName: localStorage.getItem('instanceName'),
      username: localStorage.getItem('userName')
    };
    const tempRefusedBiometrics = localStorage.getItem('refusedBiometrics');
    const tempDateFormat = localStorage.getItem('selectedDateFormat');
    const userObjectInFolderHref = localStorage.getItem('userObjectInFolderHref');
    this.deleteDeviceEndpoint(localStorage.getItem('previousARN'), userObjectInFolderHref).subscribe(
      endPointDeleted => {
        console.log('endPointDeleted: ', endPointDeleted);
      },
      error => {
        console.log('endPointDeleted error: ', error);
        this.presentToast('You may still receive some notifications.');
        // this.menu.close();
        localStorage.clear();
        localStorage.setItem('loginInfo', JSON.stringify(tempLoginInfo));
        localStorage.setItem('logoutInitiated', 'true');
        if (tempRefusedBiometrics !== null) {
          localStorage.setItem('refusedBiometrics', tempRefusedBiometrics);
        }
        if (tempDateFormat !== null) {
          localStorage.setItem('selectedDateFormat', tempDateFormat);
        }
        this.access_token = null;
        location.assign(this.originURI);
        this.loadingService.loadingDismiss();
      },
      () => {
        console.log('endPointDeleted successfully');
        // this.menu.close();
        localStorage.clear();
        localStorage.setItem('loginInfo', JSON.stringify(tempLoginInfo));
        localStorage.setItem('logoutInitiated', 'true');
        if (tempRefusedBiometrics !== null) {
          localStorage.setItem('refusedBiometrics', tempRefusedBiometrics);
        }
        if (tempDateFormat !== null) {
          localStorage.setItem('selectedDateFormat', tempDateFormat);
        }
        this.access_token = null;
        this.loadingService.loadingDismiss();
        location.assign(this.originURI);
      }
    );
  }

  deleteDeviceEndpoint(ArnValue: string, userObjectInFolderHref) {
    const callPath: string =
      this.APIURL + userObjectInFolderHref + '/mobileplatformappendpoint?arn=' + encodeURIComponent(ArnValue);

    return this.httpClient.delete(callPath, this.returnHttpOptions());
  }

  unlinkObject(object: Feenics.Keep.WebApi.Model.BaseInfo, linkedObjectKey: string, relation: string) {
    const callPath = `${this.APIURL}${object.Href}/connections/${relation}/${linkedObjectKey}`;

    return this.httpClient.delete<any>(callPath, this.returnHttpOptions());
  }

  assignConnectedObjectLinks(object: Feenics.Keep.WebApi.Model.BaseInfo, linkedObjectKey: string, relation: string, isOneToOne: boolean) {
    const callPath = `${this.APIURL}${object.Href}/connections/${relation}?isOneToOne=${isOneToOne.toString()}&relatedKey=${linkedObjectKey}`;

    return this.httpClient.put<any>(callPath, null, this.returnHttpOptions());
  }

  postImage(objectHref: string, imageInfo: localModel.localImageInfoPost) {
    const callPath = `${this.APIURL}${objectHref}/images?replaceIfExisting=True`;

    return this.httpClient.post<localModel.localImageInfoPost>(callPath, JSON.stringify(imageInfo), this.returnHttpOptions());
  }

  getImage(objectHref: string, category: string) {
    const callPath = `${this.APIURL}${objectHref}/images?category=${encodeURIComponent(category)}`;

    return this.httpClient.get<localModel.localImageInfoPost>(callPath, this.returnHttpOptions());
  }
}

export class Validation {
  // namePattern: any = /^[^0-9]+$/;
  phonePattern: any = /^(?:(?:\+?1\s*(?:[.-]\s*)?)?(?:\(\s*([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9])\s*\)|([2-9]1[02-9]|[2-9][02-8]1|[2-9][02-8][02-9]))\s*(?:[.-]\s*)?)?([2-9]1[02-9]|[2-9][02-9]1|[2-9][02-9]{2})\s*(?:[.-]\s*)?([0-9]{4})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
  emailPattern: any = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
  MACPattern: any = /^[0-9a-f]{1,2}([\.:-])(?:[0-9a-f]{1,2}\1){4}[0-9a-f]{1,2}$/;
  // pinPattern: any = /(\d){4,15}/;

  countries: string[] = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'The   Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burma', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Democratic Republic of the Congo', 'Republic of the Congo', 'Costa Rica', 'Ivory Coast', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic', 'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'East Timor', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'The Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Republic of Ireland', 'Israel', 'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'North Korea', 'South Korea', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Republic of Macedonia', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Federated States of Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Namibia', 'Nauru', 'Nepal', 'Kingdom of the Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'Norway', 'Oman', 'Pakistan', 'Palau', 'State of Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'São Tomé and Príncipe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Swaziland', 'Sweden', 'Switzerland', 'Syria', 'Tajikistan', 'Tanzania', 'Thailand', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe'
  ];

}

